/**
 * R-TISCALICZ operator panel data layer (single page, 2026-09-16).
 *
 * Tiscalicz does NOT mint aliases: one sellable unit per mailbox, and the scarce
 * resource is the (mailbox, site) right. ONE endpoint feeds the whole page:
 * stock counts, credential-probe state, per-site sales and the per-mailbox
 * detail ("hangi siteler için satıldı").
 *
 * Endpoint: GET /admin/providers/tiscalicz/site-usage (admin-guarded; served only
 * for a bundle that declares siteScopedRights — mail.com/gmx answer 404
 * provider_not_supported).
 */
import { callAdminApi, resolveBaseUrl, type ApiResult } from '../../api/client.js';

export interface TiscaliczStockUnits {
  readonly total: number;
  readonly available: number;
  readonly leased: number;
  readonly reserved: number;
  readonly burnedPairs: number;
  readonly mailboxesWithBurns: number;
}

export interface TiscaliczProbeState {
  /** login verified at least once (sellable) */
  readonly verified: number;
  /** imported, not yet probed (becomes sellable within ~1 min per batch) */
  readonly pending: number;
  /** login refused; re-probed after the backoff, sticky after 10 attempts */
  readonly quarantined: number;
}

export interface TiscaliczSiteUsageRow {
  readonly site: string;
  /** mail arrived — permanent, closed for everyone */
  readonly burnedMailboxes: number;
  /** sold but no mail — closed only for that customer */
  readonly servedMailboxes: number;
  /** every mailbox ever sold for this site (burned + served) */
  readonly soldMailboxes: number;
  /** burnedMailboxes / units.total × 100 — "stokların %kaçı bu site için". */
  readonly shareOfStock: number;
  readonly lastBurnedAt: string | null;
  readonly lastSoldAt: string | null;
}

export interface TiscaliczMailboxRow {
  readonly address: string;
  readonly unitStatus: string;
  readonly accountStatus: string;
  readonly sessionState: string;
  readonly verified: boolean;
  readonly hasCredential: boolean;
  readonly consecutiveErrors: number;
  readonly cooldownUntil: string | null;
  readonly lastSeenAt: string | null;
  readonly burnedSites: readonly string[];
  readonly servedSites: readonly string[];
  readonly lastBurnedAt: string | null;
  readonly lastServedAt: string | null;
}

export interface TiscaliczSiteUsage {
  readonly providerId: string;
  readonly units: TiscaliczStockUnits;
  readonly accounts: number;
  readonly probe?: TiscaliczProbeState;
  readonly sites: readonly TiscaliczSiteUsageRow[];
  readonly mailboxes?: readonly TiscaliczMailboxRow[];
}

export async function fetchTiscaliczSiteUsage(
  signal?: AbortSignal,
): Promise<ApiResult<TiscaliczSiteUsage>> {
  return callAdminApi<TiscaliczSiteUsage>(
    { baseUrl: resolveBaseUrl(), signal },
    { path: '/admin/providers/tiscalicz/site-usage' },
  );
}

/** Pure helper (unit-tested): share of a partial stock count, one decimal. */
export function sharePercent(part: number, total: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** Sellable share of the whole stock, one decimal. */
export function availableShare(units: TiscaliczStockUnits): number {
  return sharePercent(units.available, units.total);
}

/** Operator-facing label for a mailbox's credential/probe state. */
export function mailboxStateLabel(m: TiscaliczMailboxRow): {
  readonly text: string;
  readonly tone: 'success' | 'warn' | 'danger' | 'neutral';
} {
  if (m.accountStatus === 'login_failed') return { text: 'Karantina (giriş reddedildi)', tone: 'danger' };
  if (m.accountStatus !== 'active') return { text: m.accountStatus, tone: 'neutral' };
  if (!m.hasCredential) return { text: 'Kimlik yok', tone: 'danger' };
  if (m.verified) return { text: 'Doğrulandı', tone: 'success' };
  return { text: 'Doğrulama bekliyor', tone: 'warn' };
}
